import {
  AdConstraint,
  AdGroupConstraint,
  AdsCampaign,
  AdsCampaignConstraint,
  AdsGroup,
  AdsGroupAd,
  Constraints,
  ConstraintType,
  DeviceConstraint,
} from '@betterfunnel/types';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import {
  CheckIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  DeviceTabletIcon,
} from '@heroicons/react/24/outline';
import {
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ToggleButton from '../../components/Common/ToggleButton';
import { DashboardContext } from '../../contexts/DashboardContext';
import { UserContext } from '../../contexts/UserContext';
import {
  getCachedAdGroupAds,
  getCachedAdGroups,
  getCachedCampaigns,
} from '../../services/AdsAccountsService';
import { capitalize, classNames, distinct } from '../../utils';
import Breadcrumbs from '../Breadcrumbs';
import Badge from '../Common/Badge';
import Button from '../Common/Button';
import IconButton from '../Common/IconButton';
import Loader from '../Common/Loader';
import Tooltip from '../Common/Tooltip';
import { SearchField } from '../SearchField';
import { KeywordsConstraintSelectorModal } from './KeywordsConstraintSelectorModal';

interface Props {
  constraints: Constraints;
  onChange: (_: Constraints) => void;
  readOnly?: boolean;
  visibleConstraints?: ConstraintType[];
}

export default function ConstraintsForm({
  constraints,
  onChange,
  readOnly,
  visibleConstraints,
}: Props) {
  const { selectedDynamicPage } = useContext(DashboardContext);

  const [selectKeywordsModalOpen, setSelectKeywordsModalOpen] = useState(false);

  const showKeywords =
    !visibleConstraints || visibleConstraints.includes('keywords');

  const showDevices =
    !visibleConstraints || visibleConstraints.includes('devices');

  const showAds = !visibleConstraints || visibleConstraints.includes('ads');

  const showAdsGroup =
    !visibleConstraints || visibleConstraints.includes('adGroups');

  const showCampaigns =
    !visibleConstraints || visibleConstraints.includes('campaigns');

  const filters = useMemo(() => {
    if (!selectedDynamicPage?.data().connections) return;

    return Object.values(selectedDynamicPage?.data().connections ?? {}).map(
      (c) => ({
        ...(c.type === 'adsCampaign' ? { campaignId: c.id } : {}),
        adsAccountId: c.adsAccountId,
        provider: c.provider,
      }),
    );
  }, [selectedDynamicPage]);

  return (
    <div className="flex flex-col gap-4">
      <KeywordsConstraintSelectorModal
        constraints={constraints}
        open={selectKeywordsModalOpen}
        onClose={() => {
          setSelectKeywordsModalOpen(false);
        }}
        onSave={onChange}
      />
      {showKeywords && (
        <ConstraintsTypeSection
          type="keywords"
          title="Keywords"
          tooltip={({ id }) => `Keyword: ${id}`}
          chipTitle={(item) => item.keyword}
          emptyMessage="No keywords selected"
          constraints={constraints}
          onChange={onChange}
          // filters={filters}
          selectButton={
            <Button
              className="h-full"
              variant="ternary"
              color="primary"
              size="small"
              onClick={() => {
                setSelectKeywordsModalOpen(true);
              }}
            >
              Select
            </Button>
          }
        />
      )}
      {showDevices && (
        <div className="flex items-center gap-2">
          <div className="h-[1px] grow bg-gray-200"></div>
          <div>
            <p className="text-xs font-medium italic text-gray-900">and</p>
          </div>
          <div className="h-[1px] grow bg-gray-200"></div>
        </div>
      )}
      {showDevices && (
        <div className="mb-2 flex w-full flex-col gap-2">
          <p className="text-sm font-medium text-gray-900">Devices</p>
          <ToggleButton<DeviceConstraint | 'all'>
            multi
            disabled={readOnly}
            items={[
              { value: 'all', title: 'All Devices' },
              { value: 'mobile', title: 'Mobile', icon: DevicePhoneMobileIcon },
              { value: 'tablet', title: 'Tablet', icon: DeviceTabletIcon },
              { value: 'desktop', title: 'Desktop', icon: ComputerDesktopIcon },
            ]}
            value={!constraints.devices?.length ? ['all'] : constraints.devices}
            onChange={(newDevices) => {
              if (constraints.devices?.length && newDevices.includes('all')) {
                onChange({ ...constraints, devices: [] });
              } else {
                onChange({
                  ...constraints,
                  devices: newDevices
                    .filter((x) => x !== 'all')
                    .map((x) => x satisfies DeviceConstraint),
                });
              }
            }}
          />
        </div>
      )}
      {showAds && (
        <div className="flex items-center gap-2">
          <div className="h-[1px] grow bg-gray-200"></div>
          <div>
            <p className="text-xs font-medium italic text-gray-900">and</p>
          </div>
          <div className="h-[1px] grow bg-gray-200"></div>
        </div>
      )}
      {showAds && (
        <ConstraintsAdsAccountItemsSection
          type="ads"
          title="Ads"
          tooltip={({ id }) => `Ad Id: ${id}`}
          emptyMessage="No ads selected"
          constraints={constraints}
          onChange={onChange}
          filters={filters}
        />
      )}
      {showAdsGroup && (
        <div className="flex items-center gap-2">
          <div className="h-[1px] grow bg-gray-200"></div>
          <div>
            <p className="text-xs font-medium italic text-gray-900">and</p>
          </div>
          <div className="h-[1px] grow bg-gray-200"></div>
        </div>
      )}
      {showAdsGroup && (
        <ConstraintsAdsAccountItemsSection
          type="adGroups"
          title="Ad Groups"
          tooltip={({ id }) => `Ad Group Id: ${id}`}
          emptyMessage="No ad groups selected"
          constraints={constraints}
          onChange={onChange}
          filters={filters}
        />
      )}
      {showCampaigns && (
        <div className="flex items-center gap-2">
          <div className="h-[1px] grow bg-gray-200"></div>
          <div>
            <p className="text-xs font-medium italic text-gray-900">and</p>
          </div>
          <div className="h-[1px] grow bg-gray-200"></div>
        </div>
      )}
      {showCampaigns && (
        <ConstraintsAdsAccountItemsSection
          type="campaigns"
          title="Campaigns"
          tooltip={({ id }) => `Campaign Id: ${id}`}
          emptyMessage="No campaigns selected"
          constraints={constraints}
          onChange={onChange}
          filters={filters}
        />
      )}
    </div>
  );
}

const MAX_CHIPS = 10;

interface ConstraintsTypeSectionProps<T extends ConstraintType> {
  type: T;
  title: string;
  emptyMessage: string;
  tooltip: string | ((item: NonNullable<Constraints[T]>[0]) => string);
  constraints: Constraints;
  chipTitle: (item: NonNullable<Constraints[T]>[0]) => string;
  onChange: (_: Constraints) => void;
  selectButton: ReactNode;
}

const ConstraintsTypeSection = <
  T extends Extract<
    ConstraintType,
    'campaigns' | 'adGroups' | 'ads' | 'keywords'
  >,
>({
  type,
  title,
  emptyMessage,
  tooltip,
  constraints,
  chipTitle,
  onChange,
  selectButton,
}: ConstraintsTypeSectionProps<T>) => {
  const selected = constraints[type];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex w-full items-center justify-end gap-2">
        <div className="flex-grow text-sm font-medium text-gray-900">
          {title}
        </div>
        {selectButton}
      </div>
      <div className="flex flex-1 flex-wrap gap-2">
        {!selected?.length && (
          <p className="text-sm text-gray-500">{emptyMessage}</p>
        )}
        {selected?.slice(0, MAX_CHIPS).map((item) => (
          <Tooltip
            key={item.id}
            text={typeof tooltip === 'string' ? tooltip : tooltip(item)}
          >
            <Badge
              key={item.id}
              hideDot
              title={chipTitle(item)}
              showDeleteButton
              onDelete={() => {
                onChange({
                  ...constraints,
                  [type]: selected?.filter((_item) => _item.id !== item.id),
                });
              }}
            />
          </Tooltip>
        ))}
        {(selected?.length ?? 0) > MAX_CHIPS && (
          <Popover>
            <PopoverButton
              as={Button}
              variant="ternary"
              size="x-small"
              color="primary"
            >
              Show More
            </PopoverButton>

            <PopoverPanel
              transition
              anchor="top end"
              modal
              className="z-10 h-80 w-96 transition duration-200 ease-in-out data-[closed]:translate-y-1 data-[closed]:opacity-0"
            >
              <div
                className="flex h-full flex-col divide-y rounded-lg border border-gray-300 bg-white p-3 py-2.5 pb-0 shadow-sm"
                style={{ height: 'calc(100% - 0.625rem)' }}
              >
                <div className="pb-2 font-medium">Selected {title}</div>
                <div className="flex flex-1 flex-col justify-stretch gap-2 overflow-scroll py-2">
                  {selected?.map((item) => (
                    <Button
                      key={item.id}
                      variant="secondary"
                      size="normal"
                      className="relative inline-block justify-start pe-9 ps-4 text-start"
                    >
                      <span>
                        {capitalize(
                          ('name' in item && item.name) ||
                            ('keyword' in item && item.keyword) ||
                            ('headlines' in item &&
                              item.headlines
                                .sort(
                                  (h1: any, h2: any) =>
                                    h1.metrics.impressions -
                                    h2.metrics.impressions,
                                )
                                .map((h: any) => h.text)
                                ?.slice(0, 3)
                                .join(' | ')) ||
                            'Ad',
                        )}
                      </span>{' '}
                      <span className="text-xs text-gray-400">- {item.id}</span>
                    </Button>
                  ))}
                </div>
              </div>
            </PopoverPanel>
          </Popover>
        )}
      </div>
    </div>
  );
};

type ConstraintsAdsAccountItemsSectionProps<
  T extends Extract<ConstraintType, 'campaigns' | 'adGroups' | 'ads'>,
> = Omit<ConstraintsTypeSectionProps<T>, 'selectButton' | 'chipTitle'> & {
  filters?: {
    campaignId?: string;
    adsAccountId: string;
    provider: 'google' | 'facebook';
  }[];
};

const ConstraintsAdsAccountItemsSection = <
  T extends Extract<ConstraintType, 'campaigns' | 'adGroups' | 'ads'>,
>({
  type,
  title,
  emptyMessage,
  tooltip,
  constraints,
  onChange,
  filters,
}: ConstraintsAdsAccountItemsSectionProps<T>) => {
  return (
    <ConstraintsTypeSection
      type={type}
      title={title}
      emptyMessage={emptyMessage}
      tooltip={tooltip}
      chipTitle={(item) =>
        'name' in item ? item.name : (item.headlines?.[0]?.text ?? 'Ad')
      }
      constraints={constraints}
      onChange={onChange}
      selectButton={
        <AdsAccountsDataConstraintPopover
          type={type}
          selected={(constraints[type] || []) as any[]}
          onChange={(items: any) => {
            onChange({
              ...constraints,
              [type]: items,
            });
          }}
          filters={filters}
        />
      }
    />
  );
};

type AdsItemsConstraintSelectorCampaigns = {
  type: Extract<ConstraintType, 'campaigns'>;
  selected: AdsCampaignConstraint[];
  onChange: (_: AdsCampaignConstraint[]) => void;

  filters?: {
    campaignId?: string;
    adsAccountId: string;
    provider: 'google' | 'facebook';
  }[];
};

type AdsItemsConstraintSelectorAdGroups = {
  type: Extract<ConstraintType, 'adGroups'>;
  selected: AdGroupConstraint[];
  onChange: (_: AdGroupConstraint[]) => void;

  filters?: {
    campaignId?: string;
    adsAccountId: string;
    provider: 'google' | 'facebook';
  }[];
};

type AdsItemsConstraintSelectorAds = {
  type: Extract<ConstraintType, 'ads'>;
  selected: AdConstraint[];
  onChange: (_: AdConstraint[]) => void;

  filters?: {
    campaignId?: string;
    adsAccountId: string;
    provider: 'google' | 'facebook';
  }[];
};

type AdsAccountsDataConstraintSelectorProps =
  | AdsItemsConstraintSelectorCampaigns
  | AdsItemsConstraintSelectorAdGroups
  | AdsItemsConstraintSelectorAds;

const AdsAccountsDataConstraintPopover = (
  props: AdsAccountsDataConstraintSelectorProps,
) => {
  return (
    <Popover className="relative h-full">
      <PopoverButton
        as={Button}
        className="h-full"
        variant="ternary"
        color="primary"
        size="small"
      >
        Select
      </PopoverButton>

      <PopoverPanel
        transition
        anchor="top end"
        modal
        className="z-10 h-80 w-96 transition duration-200 ease-in-out data-[closed]:translate-y-1 data-[closed]:opacity-0"
      >
        <div
          className="h-full rounded-lg border border-gray-300 bg-white p-3 py-2.5 pb-0 shadow-sm"
          style={{ height: 'calc(100% - 0.625rem)' }}
        >
          <AdsAccountsDataConstraint {...props} />
        </div>
      </PopoverPanel>
    </Popover>
  );
};

const AdsAccountsDataConstraint = ({
  type,
  selected,
  onChange,
  filters,
}: AdsAccountsDataConstraintSelectorProps) => {
  const { selectedAccount } = useContext(UserContext);

  const [campaigns, setCampaigns] = useState<AdsCampaign[]>();
  const [campaignAdGroups, setCampaignAdGroups] = useState<AdsGroup[]>();
  const [adGroupAds, setAdGroupAds] = useState<AdsGroupAd[]>();

  const [currentCampaign, setCurrentCampaign] = useState<AdsCampaign>();
  const [currentAdGroup, setCurrentAdGroup] = useState<AdsGroup>();
  const [error, setError] = useState<string>();

  const [showSearch, setShowSearch] = useState(false);
  const [searchQueries, setSearchQueries] = useState<
    Record<typeof type, string>
  >({} as any);

  const reset = useCallback(() => {
    setCurrentCampaign(undefined);
    setCurrentAdGroup(undefined);
    setCampaigns(undefined);
    setCampaignAdGroups(undefined);
    setAdGroupAds(undefined);
    setError(undefined);
  }, []);

  useEffect(() => {
    reset();
  }, [type, filters, reset]);

  useEffect(() => {
    setCampaigns(undefined);
    setError(undefined);

    if (!selectedAccount) return;

    getCachedCampaigns(
      selectedAccount.id,
      distinct(filters?.map((f) => f.provider) ?? ['google']),
      filters,
    )
      .then((data) => {
        setCampaigns(data);
      })
      .catch((e) => {
        Sentry.captureException(e);
        setError('Failed to fetch campaigns');
      });
  }, [filters, selectedAccount]);

  useEffect(() => {
    setCampaignAdGroups(undefined);
    setError(undefined);

    if (!selectedAccount || !currentCampaign) return;

    getCachedAdGroups(
      selectedAccount.id,
      currentCampaign.provider,
      currentCampaign.adsAccountId,
      [currentCampaign.id],
    )
      .then((data) => {
        setCampaignAdGroups(data);
      })
      .catch((e) => {
        Sentry.captureException(e);
        setError('Failed to fetch ad groups');
      });
  }, [currentCampaign, selectedAccount]);

  useEffect(() => {
    setAdGroupAds(undefined);
    setError(undefined);

    if (!selectedAccount || !currentAdGroup || !currentCampaign) return;

    getCachedAdGroupAds(
      selectedAccount.id,
      currentAdGroup.provider,
      currentAdGroup.adsAccountId,
      [currentCampaign.id],
      [currentAdGroup.id],
    )
      .then((data) => {
        setAdGroupAds(data);
      })
      .catch((e) => {
        Sentry.captureException(e);
        setError('Failed to fetch ads');
      });
  }, [currentCampaign, currentAdGroup, selectedAccount]);

  const currentLevel: 'campaigns' | 'adGroups' | 'ads' = useMemo(() => {
    return currentAdGroup ? 'ads' : currentCampaign ? 'adGroups' : 'campaigns';
  }, [currentCampaign, currentAdGroup]);

  const searchQuery = useMemo(
    () => searchQueries[currentLevel],
    [searchQueries, currentLevel],
  );

  const currentList = useMemo(() => {
    switch (currentLevel) {
      case 'campaigns':
        return campaigns?.filter((c) =>
          c.name.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
        );
      case 'adGroups':
        return campaignAdGroups?.filter((c) =>
          c.name.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
        );
      case 'ads':
        return adGroupAds?.filter(
          (c) =>
            c.headlines?.some((h: any) =>
              h.text.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
            ) ||
            c.descriptions?.some((d: any) =>
              d.text.toLowerCase().includes(searchQuery?.toLowerCase() || ''),
            ),
        );
    }
  }, [currentLevel, campaigns, campaignAdGroups, adGroupAds, searchQuery]);

  return (
    <div className="relative flex h-full flex-col gap-2">
      <div className={classNames('border-b', showSearch && 'pb-2')}>
        <div className="flex justify-between gap-2">
          <Breadcrumbs
            breadcrumbs={[
              ...(currentLevel === 'campaigns'
                ? [{ name: 'Campaigns', href: '', current: true }]
                : []),

              ...(currentLevel === 'adGroups'
                ? [
                    {
                      name: currentCampaign?.name ?? '',
                      href: '',
                      onClick: () => setCurrentCampaign(undefined),
                    },
                    { name: 'Ad Groups', href: '', current: true },
                  ]
                : []),

              ...(currentLevel === 'ads'
                ? [
                    {
                      name: currentCampaign?.name ?? '',
                      href: '',
                      onClick: () => {
                        setCurrentAdGroup(undefined);
                        setCurrentCampaign(undefined);
                      },
                    },
                    {
                      name: currentAdGroup?.name ?? '',
                      href: '',
                      onClick: () => setCurrentAdGroup(undefined),
                    },
                    { name: 'Ads', href: '', current: true },
                  ]
                : []),
            ]}
          />
          <IconButton
            className="relative"
            variant="ternary"
            onClick={() => {
              setShowSearch((x) => !x);
            }}
          >
            <MagnifyingGlassIcon className="size-4 text-gray-600" />
          </IconButton>
        </div>

        {showSearch && (
          <SearchField
            value={searchQuery || ''}
            onChange={(e) => {
              setSearchQueries((d) => ({
                ...d,
                [currentLevel]: e.target.value,
              }));
            }}
          />
        )}
      </div>

      <div className="flex h-full w-full flex-col gap-1 overflow-scroll pb-3">
        {!currentList && !error && (
          <div className="flex h-full w-full flex-col items-center justify-center">
            <Loader />
          </div>
        )}
        {!currentList && !!error && (
          <div className="flex h-full w-full flex-col items-center justify-center">
            <p className="text-center text-red-500">{error}</p>
          </div>
        )}
        {currentList?.length === 0 && <p>No items found</p>}
        {currentList?.map((item) => {
          const itemSeleted = selected.some((s) => s.id === item.id);
          return (
            <Button
              key={item.id}
              variant={itemSeleted ? 'primary' : 'secondary'}
              size="normal"
              onClick={() => {
                if (currentLevel === type) {
                  onChange(
                    itemSeleted
                      ? selected.filter((t) => t.id !== item.id)
                      : ([...selected, item] as any),
                  );
                } else if (currentLevel === 'campaigns') {
                  setCurrentCampaign(item as AdsCampaign);
                } else if (currentLevel === 'adGroups') {
                  setCurrentAdGroup(item as AdsGroup);
                }
              }}
              className="relative justify-start pe-9 ps-4 text-start"
            >
              {currentLevel === type ? (
                <CheckIcon
                  className={classNames(
                    'absolute right-2 h-4 w-4',
                    itemSeleted ? 'visible' : 'invisible',
                  )}
                />
              ) : (
                <ChevronRightIcon
                  className={classNames(
                    'absolute right-2 h-4 w-4 text-gray-400',
                  )}
                />
              )}

              {capitalize(
                ('name' in item && item.name) ||
                  ('headlines' in item &&
                    item.headlines
                      .sort(
                        (h1: any, h2: any) =>
                          h1.metrics.impressions - h2.metrics.impressions,
                      )
                      .map((h: any) => h.text)
                      ?.slice(0, 3)
                      .join(' | ')) ||
                  'Ad',
              )}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
